<script>
    import {
        DxDataGrid,
        DxColumn,
        DxFilterRow,
        DxHeaderFilter,
        DxFilterPanel,
        DxSearchPanel,
        DxPager,
        DxPaging,
        DxExport,
        DxToolbar,
        DxItem
    } from 'devextreme-vue/data-grid';
    import tinyPumperIncidentsService from '@/services/tinyPumperIncidents.service';
    import sensorsService from '@/services/sensors.service';
    import ModalService from '@/services/core/modal.service';
    import utilsService from '@/services/core/utils.service';
    import ActionsDropdown from '@/components/shared/ActionsDropdown';
    import TinyPumperIncidentTotals from '@/components/tinyPumper/TinyPumperIncidentTotals';
    import TinyPumperIncidentTriage from '@/components/tinyPumper/TinyPumperIncidentTriage';
    import TinyPumperFleetGrid from '@/components/tinyPumper/TinyPumperFleetGrid';
    import TinyPumperAlertSwitch from '@/components/tinyPumper/TinyPumperAlertSwitch';
    import TinyPumperHealthPolicyPanel from '@/components/tinyPumper/TinyPumperHealthPolicyPanel';
    import TinyPumperToday from '@/components/tinyPumper/TinyPumperToday';
    import HealthCheckSnoozeModal from '@/components/modals/healthCheck/HealthCheckSnoozeModal';
    import {
        SENSOR_INCIDENT_CAUSE_LABELS,
        SENSOR_INCIDENT_KIND_LABELS,
        SENSOR_INCIDENT_SCOPE_LABELS,
        SENSOR_INCIDENT_SEVERITY_LABELS,
        SENSOR_SNOOZE_REASON_LABELS,
        SensorIncidentCause,
        SensorIncidentSeverity,
        SensorSnoozeReason
    } from '@/enums/TinyPumper';
    import { escapeHtml } from '@/helpers/html';

    const TAB_TODAY = 'today';
    const TAB_INCIDENTS = 'incidents';
    const TAB_TRIAGE = 'triage';
    const TAB_FLEET = 'fleet';

    /**
     * The TinyPumper incident dashboard. Slack carries one short pointer per incident and the detail
     * and the actions live here. Both grids are server-side. The incident table only grows, so there
     * is no load-everything path on this page at all.
     */
    export default {
        name: 'TinyPumperHealth',
        components: {
            DxDataGrid,
            DxColumn,
            DxFilterRow,
            DxHeaderFilter,
            DxFilterPanel,
            DxSearchPanel,
            DxPager,
            DxPaging,
            DxExport,
            DxToolbar,
            DxItem,
            ActionsDropdown,
            TinyPumperIncidentTotals,
            TinyPumperIncidentTriage,
            TinyPumperFleetGrid,
            TinyPumperAlertSwitch,
            TinyPumperHealthPolicyPanel,
            TinyPumperToday
        },
        data() {
            return {
                activeTab: TAB_TODAY,
                dataSource: tinyPumperIncidentsService.getGridDataSource(false),
                // null, not {} — the cards must be able to tell "no answer yet" from "answered
                // zero". An empty object renders as a clean bill of health before anything loads.
                totals: null,
                totalsFailed: false,
                isLoadingTotals: false,
                isIncludingClosed: false,
                focusedIncident: null,
                busyIncidentId: null
            };
        },
        computed: {
            getTabs() {
                return [
                    { key: TAB_TODAY, label: 'Today', iconClass: 'bi-sunrise' },
                    { key: TAB_INCIDENTS, label: 'All incidents', iconClass: 'bi-exclamation-octagon' },
                    { key: TAB_TRIAGE, label: 'Triage', iconClass: 'bi-signpost-split' },
                    { key: TAB_FLEET, label: 'Fleet', iconClass: 'bi-plugin' }
                ];
            },
            hasFocusedIncident() {
                return !!this.focusedIncident;
            },
            isToday() {
                return this.activeTab === TAB_TODAY;
            }
        },
        methods: {
            isIngestionFailure(row) {
                return row.cause === SensorIncidentCause.IngestionFailure;
            },
            causeLabel(value) {
                return SENSOR_INCIDENT_CAUSE_LABELS[value] ?? '—';
            },
            kindLabel(value) {
                return SENSOR_INCIDENT_KIND_LABELS[value] ?? '—';
            },
            scopeLabel(value) {
                return SENSOR_INCIDENT_SCOPE_LABELS[value] ?? '—';
            },
            severityLabel(value) {
                return SENSOR_INCIDENT_SEVERITY_LABELS[value] ?? '—';
            },
            severityClass(value) {
                switch (value) {
                    case SensorIncidentSeverity.Critical:
                        return 'text-bg-danger';
                    case SensorIncidentSeverity.High:
                        return 'text-bg-warning';
                    case SensorIncidentSeverity.Medium:
                        return 'text-bg-primary';
                    default:
                        return 'text-bg-secondary';
                }
            },
            // Read off openedAt rather than shipped as its own column: the two sort identically, and
            // openedAt is the one the database can index.
            ageDays(openedAt) {
                return Math.max(0, Math.floor((Date.now() - new Date(openedAt)) / (1000 * 60 * 60 * 24)));
            },
            // The three signals the correlator stated the cause from, as one line. A row with none of
            // them reached its cause from the silence alone.
            evidenceLabel(row) {
                const parts = [];

                if (row.batterySlopePercentPerDay !== null && row.batterySlopePercentPerDay !== undefined) {
                    const slope = Number(row.batterySlopePercentPerDay);
                    parts.push(`${slope > 0 ? '+' : ''}${slope.toFixed(1)}%/day battery`);
                }

                if (row.gatewayLastHeardAt) {
                    parts.push(`gateway heard ${new Date(row.gatewayLastHeardAt).toLocaleString()}`);
                }

                if (row.fleetReadOutcome) parts.push(`fleet read ${row.fleetReadOutcome}`);

                return parts.length ? parts.join(' · ') : 'Stated from silence — no vendor evidence';
            },
            snoozeLabel(row) {
                if (!row.isSnoozed) return row.snoozedBy ? 'Expired' : '—';

                const reason = SENSOR_SNOOZE_REASON_LABELS[row.snoozeReason] || 'Snoozed';
                return `${reason} · until ${new Date(row.snoozedUntil).toLocaleDateString()}`;
            },
            levelconLink(row) {
                return sensorsService.levelconDashboardLink(row.levelconId);
            },
            loadTotals() {
                this.isLoadingTotals = true;
                this.totalsFailed = false;

                return (
                    tinyPumperIncidentsService
                        .totals()
                        .then(totals => {
                            this.totals = totals ?? {};
                        })
                        // Without this the cards keep their zeros and go on asserting that nothing is wrong.
                        .catch(() => {
                            this.totals = null;
                            this.totalsFailed = true;
                        })
                        .finally(() => {
                            this.isLoadingTotals = false;
                        })
                );
            },
            reloadGrid() {
                this.$refs.incidentGrid?.instance?.refresh();
                this.$refs.today?.load();
                return this.loadTotals();
            },
            openVisitBooked(row) {
                ModalService.show(HealthCheckSnoozeModal, {
                    incident: row,
                    reason: SensorSnoozeReason.VisitScheduled,
                    days: 3,
                    title: 'Visit booked',
                    untilLabel: 'Visit date',
                    submitText: 'Save',
                    onSaveSuccess: () => this.reloadGrid()
                });
            },
            openWaitingOnClient(row) {
                ModalService.show(HealthCheckSnoozeModal, {
                    incident: row,
                    reason: SensorSnoozeReason.WaitingOnClient,
                    days: 2,
                    title: 'Waiting on client',
                    untilLabel: 'Check back on',
                    submitText: 'Save',
                    onSaveSuccess: () => this.reloadGrid()
                });
            },
            showIncident(row) {
                this.activeTab = TAB_INCIDENTS;
                this.$refs.incidentGrid?.instance?.filter(['id', '=', row.id]);
            },
            showAlerting() {
                this.activeTab = TAB_INCIDENTS;
            },
            toggleClosed(isIncluded) {
                this.isIncludingClosed = isIncluded;
                this.dataSource = tinyPumperIncidentsService.getGridDataSource(isIncluded);
            },
            selectTab(key) {
                this.activeTab = key;
            },
            filterByCause(cause) {
                this.activeTab = TAB_INCIDENTS;
                this.$refs.incidentGrid?.instance?.filter(['cause', '=', cause]);
            },
            clearGridFilter() {
                this.$refs.incidentGrid?.instance?.clearFilter();
            },
            openSnooze(row) {
                ModalService.show(HealthCheckSnoozeModal, {
                    incident: row,
                    onSaveSuccess: () => this.reloadGrid()
                });
            },
            acknowledge(row) {
                this.busyIncidentId = row.id;

                tinyPumperIncidentsService
                    .acknowledge(row.id)
                    .then(() => {
                        utilsService.showToast('Acknowledged.', 'success');
                        return this.reloadGrid();
                    })
                    .catch(() => utilsService.showToast('Could not acknowledge that incident.', 'warn'))
                    .finally(() => {
                        this.busyIncidentId = null;
                    });
            },
            decommission(row) {
                if (!row.sensorId) {
                    utilsService.showToast('Only a sensor-scope incident names a single device to decommission.', 'warn');
                    return;
                }

                ModalService.confirm({
                    title: 'Decommission sensor',
                    // ConfirmModal renders msg with v-html, and sensorName comes from the database
                    // by way of the vendor sync — a sensor named with markup would execute here, in
                    // the portal, for the operator who holds the most privilege. Escaped at the one
                    // place that builds the string.
                    msg: `Take <b>${escapeHtml(row.sensorName || `sensor ${row.sensorId}`)}</b> out of service permanently?<br><br>It stops reporting, stops producing incidents, and its open incidents are closed. Re-enable it from the customer app if it comes back.`,
                    btnTextOk: 'Decommission',
                    onSuccess: () => {
                        this.busyIncidentId = row.id;

                        tinyPumperIncidentsService
                            .decommission(row.sensorId)
                            .then(() => {
                                utilsService.showToast('Sensor decommissioned.', 'success');
                                return this.reloadGrid();
                            })
                            .catch(() => utilsService.showToast('Could not decommission that sensor.', 'warn'))
                            .finally(() => {
                                this.busyIncidentId = null;
                            });
                    }
                });
            },
            openInLevelcon(row) {
                const link = this.levelconLink(row);
                if (!link) {
                    utilsService.showToast('This incident has no Levelcon asset to open.', 'warn');
                    return;
                }

                window.open(link, '_blank', 'noopener');
            },
            // The Slack pointer messages link to ?incident={id}. Fetched on its own rather than
            // hunted for in the grid: the incident may sit on page forty of whatever sort the
            // operator last used, and a deep link that lands on page one is not a deep link.
            focusDeepLinkedIncident() {
                const id = Number(this.$route?.query?.incident);
                if (!id) return;

                this.activeTab = TAB_INCIDENTS;

                tinyPumperIncidentsService.getById(id).then(incident => {
                    this.focusedIncident = incident || null;

                    if (incident) this.$refs.incidentGrid?.instance?.filter(['id', '=', incident.id]);
                });
            },
            dismissFocus() {
                this.focusedIncident = null;
                this.clearGridFilter();
            }
        },
        mounted() {
            this.loadTotals();
            this.focusDeepLinkedIncident();
        },
        watch: {
            '$route.query.incident'() {
                this.focusDeepLinkedIncident();
            }
        }
    };
</script>

<template>
    <div class="main-content-header">
        <h3>
            <i class="bi-activity"></i>
            Tiny Pumper Health
        </h3>
    </div>
    <div class="main-content-body">
        <!-- Page level, and above the totals on purpose. Inside a tab panel the muted state vanished
             the moment anyone opened the Fleet tab, and below the KPI row a reader met "Open 0 —
             nothing on us right now" before learning that nobody is being told. Muting is not a view
             control that belongs beside Refresh and Include closed: it changes what the world outside
             this screen learns, so it sits with the page and precedes every number it would
             otherwise contradict. On Today the same state is the "Alerts muted" button in its summary line. -->
        <TinyPumperAlertSwitch v-if="!isToday" class="mb-3" />

        <!-- Beside the switch because they answer the same question from opposite sides: the switch
             says whether anything is being sent, the panel says what the engine had to see before it
             would send anything. Collapsed by default — it is a reference, not a headline. -->
        <TinyPumperHealthPolicyPanel v-if="!isToday" class="mb-3" />

        <TinyPumperIncidentTotals
            v-if="!isToday"
            :totals="totals"
            :isLoading="isLoadingTotals"
            :hasFailed="totalsFailed"
            @filter-cause="filterByCause"
            class="mb-3"
        />

        <ul class="nav nav-tabs mb-3">
            <li v-for="tab in getTabs" :key="`tp-health-tab-${tab.key}`" class="nav-item">
                <button type="button" class="nav-link" :class="{ active: activeTab === tab.key }" @click="selectTab(tab.key)">
                    <i :class="`bi ${tab.iconClass} me-2`"></i>
                    {{ tab.label }}
                </button>
            </li>
        </ul>

        <div v-show="activeTab === 'today'">
            <TinyPumperToday
                ref="today"
                :busyIncidentId="busyIncidentId"
                @book-visit="openVisitBooked"
                @wait-on-client="openWaitingOnClient"
                @handled="acknowledge"
                @decommission="decommission"
                @open-levelcon="openInLevelcon"
                @show-incident="showIncident"
                @open-alerting="showAlerting"
            />
        </div>

        <div v-show="activeTab === 'incidents'">
            <div v-if="hasFocusedIncident" class="alert alert-info d-flex align-items-center gap-3">
                <i class="bi bi-link-45deg fs-5"></i>
                <div class="flex-grow-1">
                    Showing incident <b>#{{ focusedIncident.id }}</b> — {{ causeLabel(focusedIncident.cause) }} ·
                    {{ focusedIncident.companyName || '—' }} · {{ focusedIncident.affectedSensorCount }} sensor(s)
                </div>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="dismissFocus">Show all</button>
            </div>

            <DxDataGrid
                ref="incidentGrid"
                class="admin-grid"
                :data-source="dataSource"
                :remote-operations="true"
                :row-alternation-enabled="true"
                :column-auto-width="true"
                :allow-column-reordering="true"
                :show-borders="false"
                :word-wrap-enabled="true"
                no-data-text="No open incidents. Every active sensor is reporting."
            >
                <DxToolbar>
                    <DxItem location="before">
                        <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <div class="form-check form-switch mb-0">
                                <input
                                    id="tp-health-include-closed"
                                    class="form-check-input"
                                    type="checkbox"
                                    :checked="isIncludingClosed"
                                    @change="event => toggleClosed(event.target.checked)"
                                />
                                <label class="form-check-label text-nowrap" for="tp-health-include-closed">Include closed</label>
                            </div>
                            <button type="button" class="btn btn-outline-secondary text-nowrap" @click="reloadGrid">
                                <i class="bi bi-arrow-clockwise me-1"></i>
                                Refresh
                            </button>
                        </div>
                    </DxItem>
                    <DxItem name="exportButton" />
                    <DxItem name="searchPanel" />
                </DxToolbar>
                <DxExport :enabled="true" />
                <DxFilterRow :visible="true" />
                <DxHeaderFilter :visible="true" />
                <DxFilterPanel :visible="true" />
                <DxSearchPanel :visible="true" :width="300" placeholder="Search..." />
                <DxPaging :page-size="20" />
                <DxPager :allowed-page-sizes="[20, 50, 100, 250]" :show-page-size-selector="true" />

                <DxColumn
                    alignment="right"
                    cell-template="actionsCell"
                    css-class="px-3"
                    :allow-sorting="false"
                    :allow-filtering="false"
                    :allow-search="false"
                />
                <DxColumn data-field="severity" caption="Severity" cell-template="severityCell" />
                <DxColumn data-field="scope" caption="Scope" cell-template="scopeCell" />
                <DxColumn data-field="companyName" caption="Company" />
                <DxColumn data-field="batteryName" caption="Facility" />
                <DxColumn data-field="cause" caption="Cause" cell-template="causeCell" />
                <!-- Bound to hasEvidence, not to the three values: that is the one the database can
                     filter on, so "show me only the causes something actually measured" is a header
                     filter rather than a reason to load the table. -->
                <DxColumn data-field="hasEvidence" caption="Evidence" data-type="boolean" cell-template="evidenceCell" />
                <DxColumn data-field="affectedSensorCount" caption="Affected" data-type="number" />
                <DxColumn
                    data-field="silentSince"
                    caption="Silent since (UTC)"
                    data-type="datetime"
                    format="MM/dd/yyyy HH:mm"
                    css-class="font-monospace"
                />
                <DxColumn data-field="openedAt" caption="Age" data-type="datetime" sort-order="desc" cell-template="ageCell" />
                <DxColumn data-field="isSnoozed" caption="Snooze" cell-template="snoozeCell" />
                <DxColumn data-field="acknowledgedBy" caption="Acknowledged by" cell-template="acknowledgedCell" />
                <!-- Derived on the server from columns the row already carries, so it is not a
                     database column and nothing here may ask SQL to sort, filter or search it. -->
                <DxColumn
                    data-field="notificationStatus"
                    caption="Alerting"
                    cell-template="notificationCell"
                    :allow-sorting="false"
                    :allow-filtering="false"
                    :allow-header-filtering="false"
                    :allow-search="false"
                />

                <template #actionsCell="{ data }">
                    <ActionsDropdown
                        :aria-label="`Actions for incident ${data.data.id}`"
                        :isLoading="busyIncidentId === data.data.id"
                        :actions="{
                            Snooze: () => openSnooze(data.data),
                            Acknowledge: () => acknowledge(data.data),
                            Decommission: data.data.sensorId ? () => decommission(data.data) : false,
                            'Open in LevelCon': data.data.levelconId ? () => openInLevelcon(data.data) : false
                        }"
                    />
                </template>
                <template #severityCell="{ data }">
                    <span class="badge" :class="severityClass(data.value)">{{ severityLabel(data.value) }}</span>
                </template>
                <template #scopeCell="{ data }">
                    <div>
                        <div>{{ scopeLabel(data.value) }}</div>
                        <small class="text-muted">{{ kindLabel(data.data.kind) }}</small>
                    </div>
                </template>
                <template #causeCell="{ data }">
                    <div class="d-flex align-items-center gap-2">
                        <i v-if="isIngestionFailure(data.data)" class="bi bi-bug-fill text-danger" title="Ours to fix"></i>
                        <span>{{ causeLabel(data.value) }}</span>
                    </div>
                </template>
                <template #evidenceCell="{ data }">
                    <div v-if="data.value" class="d-flex align-items-baseline gap-2">
                        <i class="bi bi-clipboard-data text-success" title="Cause stated from vendor evidence"></i>
                        <small>{{ evidenceLabel(data.data) }}</small>
                    </div>
                    <small v-else class="text-muted">{{ evidenceLabel(data.data) }}</small>
                </template>
                <template #ageCell="{ data }">
                    <span :class="data.data.isChronic ? 'text-danger fw-bold' : ''" :title="new Date(data.value).toLocaleString()">
                        {{ ageDays(data.value) }} days
                        <i v-if="data.data.isChronic" class="bi bi-hourglass-bottom ms-1" title="Open for more than 30 days"></i>
                    </span>
                </template>
                <template #snoozeCell="{ data }">
                    <div>
                        <div>{{ snoozeLabel(data.data) }}</div>
                        <small v-if="data.data.snoozedBy" class="text-muted">{{ data.data.snoozedBy }}</small>
                    </div>
                </template>
                <template #notificationCell="{ data }">
                    <div>
                        <div class="small">{{ data.value || '—' }}</div>
                        <small v-if="data.data.notifiedAt" class="text-muted">
                            Last said {{ new Date(data.data.notifiedAt).toLocaleString() }} · {{ data.data.notifyCount }}×
                        </small>
                    </div>
                </template>
                <template #acknowledgedCell="{ data }">
                    <div v-if="data.value">
                        <div>{{ data.value }}</div>
                        <small class="text-muted">{{ new Date(data.data.acknowledgedAt).toLocaleString() }}</small>
                    </div>
                    <span v-else class="text-muted">—</span>
                </template>
            </DxDataGrid>
        </div>

        <div v-show="activeTab === 'triage'">
            <TinyPumperIncidentTriage @filter-cause="filterByCause" />
        </div>

        <div v-show="activeTab === 'fleet'">
            <TinyPumperFleetGrid :showTotals="false" />
        </div>
    </div>
</template>
